/**
 * src/app/ui/ShapesPanel.ts
 *
 * Shapes panel — custom shape preset picker.
 * Renders a clickable grid with one entry per preset the Custom Shape tool
 * (src/core/tools/CustomShapeTool.ts) supports. Clicking an entry sets the
 * active preset on the CustomShapeTool instance passed to the panel (via the
 * constructor or `setTool()`), mirroring Photoshop's Shapes panel.
 *
 * The panel is DOM-only and has no dependency on how/where it gets mounted;
 * whatever wires the live tool instance in (see blockers reported alongside
 * this change) just needs to call `setTool()` with the app's CustomShapeTool.
 */
import { CUSTOM_SHAPE_PRESETS, type CustomShapePreset, type CustomShapeTool } from "../../core/tools/CustomShapeTool";

/** "arrow-right" -> "Arrow Right" */
function presetLabel(preset: string): string {
  return preset.split("-").map((w) => (w[0] ?? "").toUpperCase() + w.slice(1)).join(" ");
}

export class ShapesPanel {
  private container: HTMLElement;
  private grid: HTMLElement;
  private tool: CustomShapeTool | null;

  constructor(tool?: CustomShapeTool | null) {
    this.tool = tool ?? null;

    this.container = document.createElement("div");
    this.container.className = "shapes-panel";
    this.container.innerHTML = `
      <div class="panel-title">Shapes</div>
      <div class="panel-body shapes-panel__grid" role="listbox" aria-label="Custom shape presets"></div>
    `;

    this.grid = this.container.querySelector(".shapes-panel__grid")!;
    this.renderItems();
  }

  private renderItems(): void {
    const activePreset = this.tool?.preset ?? null;
    this.grid.innerHTML = CUSTOM_SHAPE_PRESETS.map((preset) => {
      const label = presetLabel(preset);
      const active = preset === activePreset;
      return `
        <button
          type="button"
          class="shapes-panel__item${active ? " is-active" : ""}"
          data-preset="${preset}"
          role="option"
          aria-selected="${active}"
          title="${label}"
        >
          <span class="shapes-panel__swatch shapes-panel__swatch--${preset}"></span>
          <span class="shapes-panel__label">${label}</span>
        </button>
      `;
    }).join("");

    this.grid.querySelectorAll<HTMLButtonElement>(".shapes-panel__item").forEach((btn) => {
      btn.addEventListener("click", () => {
        const preset = btn.dataset.preset as CustomShapePreset;
        this.selectPreset(preset);
      });
    });
  }

  private selectPreset(preset: CustomShapePreset): void {
    if (this.tool) this.tool.preset = preset;

    this.grid.querySelectorAll<HTMLButtonElement>(".shapes-panel__item").forEach((btn) => {
      const isMatch = btn.dataset.preset === preset;
      btn.classList.toggle("is-active", isMatch);
      btn.setAttribute("aria-selected", String(isMatch));
    });

    // Let any host wiring (e.g. main.ts) react to the selection without this
    // panel needing a direct reference beyond the optional `tool` field.
    this.container.dispatchEvent(
      new CustomEvent("pixelpa:shape-preset-selected", { bubbles: true, detail: { preset } })
    );
  }

  /** Attach (or detach, with null) the live CustomShapeTool this panel controls. */
  setTool(tool: CustomShapeTool | null): void {
    this.tool = tool ?? null;
    this.renderItems();
  }

  /** Currently active preset, if a tool is attached. */
  getActivePreset(): CustomShapePreset | null {
    return this.tool?.preset ?? null;
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
